import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input"]
  // HTML to fill the field with, as if pasted: a Mezco product page's
  // description the browser extension brought in (the Edit page's import).
  static values = { importHtml: String }

  connect() {
    this.inputTarget.addEventListener("paste", this.handlePaste.bind(this))
    if (this.importHtmlValue) {
      // Parsed apart from the page, so nothing in it loads or runs.
      const body = new DOMParser().parseFromString(this.importHtmlValue, "text/html").body
      this.inputTarget.value = this.htmlToMarkdown(body)
    }
  }

  handlePaste(event) {
    // Get the clipboard data
    const clipboardData = event.clipboardData || window.clipboardData
    const rtf = clipboardData.getData("text/rtf")
    const html = clipboardData.getData("text/html")
    const plain = clipboardData.getData("text/plain")

    // Always prevent default to handle the paste ourselves
    event.preventDefault()
    
    let content = ""
    
    if (rtf || html) {
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement("div")
      tempDiv.innerHTML = html || this.rtfToHtml(rtf)
      
      // Convert to markdown
      content = this.htmlToMarkdown(tempDiv)
    } else if (plain) {
      // Handle plain text by ensuring proper spacing
      content = plain
        .split(/\r?\n/)  // Split by newlines
        .map(line => line.trim())  // Trim each line
        .filter(line => line.length > 0)  // Remove empty lines
        .join("\n\n")  // Join with double newlines
    }
    
    // Insert the content at cursor position
    const start = this.inputTarget.selectionStart
    const end = this.inputTarget.selectionEnd
    const text = this.inputTarget.value
    this.inputTarget.value = text.substring(0, start) + content + text.substring(end)
    
    // Set cursor position after the inserted text
    const newCursorPos = start + content.length
    this.inputTarget.setSelectionRange(newCursorPos, newCursorPos)
  }

  rtfToHtml(rtf) {
    // Basic RTF to HTML conversion
    // This is a simplified version - you might want to use a library for more robust conversion
    return rtf
      .replace(/\\par/g, "\n")
      .replace(/\\b\s/g, "<strong>")
      .replace(/\\b0\s/g, "</strong>")
      .replace(/\\i\s/g, "<em>")
      .replace(/\\i0\s/g, "</em>")
      .replace(/\\ul\s/g, "<u>")
      .replace(/\\ulnone\s/g, "</u>")
      .replace(/\\bullet\s/g, "• ")
      .replace(/\\line/g, "<br>")
  }

  htmlToMarkdown(element) {
    let markdown = ""
    let currentBlock = ""

    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim()
        if (text) {
          currentBlock += text + " "
        }
        return
      }

      const tag = node.tagName?.toLowerCase()

      switch (tag) {
        case 'h1':
          if (currentBlock) {
            markdown += currentBlock.trim() + "\n\n"
            currentBlock = ""
          }
          markdown += `# ${node.textContent.trim()}\n\n`
          break
        case 'h2':
          if (currentBlock) {
            markdown += currentBlock.trim() + "\n\n"
            currentBlock = ""
          }
          markdown += `## ${node.textContent.trim()}\n\n`
          break
        case 'h3':
          if (currentBlock) {
            markdown += currentBlock.trim() + "\n\n"
            currentBlock = ""
          }
          markdown += `### ${node.textContent.trim()}\n\n`
          break
        case 'h4':
          if (currentBlock) {
            markdown += currentBlock.trim() + "\n\n"
            currentBlock = ""
          }
          markdown += `#### ${node.textContent.trim()}\n\n`
          break
        case 'h5':
          if (currentBlock) {
            markdown += currentBlock.trim() + "\n\n"
            currentBlock = ""
          }
          markdown += `##### ${node.textContent.trim()}\n\n`
          break
        case 'h6':
          if (currentBlock) {
            markdown += currentBlock.trim() + "\n\n"
            currentBlock = ""
          }
          markdown += `###### ${node.textContent.trim()}\n\n`
          break
        case 'p':
          if (currentBlock) {
            markdown += currentBlock.trim() + "\n\n"
            currentBlock = ""
          }
          const paragraphText = Array.from(node.childNodes)
            .map(child => child.textContent)
            .join('')
            .replace(/\s+/g, ' ')
            .trim()
          markdown += paragraphText + "\n\n"
          break
        case 'ul':
          if (currentBlock) {
            markdown += currentBlock.trim() + "\n\n"
            currentBlock = ""
          }
          Array.from(node.children).forEach(li => {
            markdown += `- ${li.textContent.trim()}\n`
          })
          markdown += "\n"
          break
        case 'ol':
          if (currentBlock) {
            markdown += currentBlock.trim() + "\n\n"
            currentBlock = ""
          }
          Array.from(node.children).forEach((li, index) => {
            markdown += `${index + 1}. ${li.textContent.trim()}\n`
          })
          markdown += "\n"
          break
        case 'li':
          if (!node.parentElement.matches('ul, ol')) {
            if (currentBlock) {
              markdown += currentBlock.trim() + "\n\n"
              currentBlock = ""
            }
            markdown += `- ${node.textContent.trim()}\n\n`
          }
          break
        case 'a':
          const href = node.getAttribute('href')
          currentBlock += `[${node.textContent.trim()}](${href})`
          break
        case 'strong':
        case 'b':
          currentBlock += `**${node.textContent.trim()}**`
          break
        case 'em':
        case 'i':
          currentBlock += `*${node.textContent.trim()}*`
          break
        case 'br':
          if (currentBlock) {
            markdown += currentBlock.trim() + "\n\n"
            currentBlock = ""
          }
          break
        default:
          Array.from(node.childNodes).forEach(processNode)
      }
    }

    // Process all nodes
    Array.from(element.childNodes).forEach(processNode)

    // Add any remaining content
    if (currentBlock) {
      markdown += currentBlock.trim() + "\n\n"
    }

    // Clean up the markdown
    return markdown
      .replace(/\n{3,}/g, '\n\n')  // Replace 3 or more newlines with 2
      .replace(/[ \t]+$/gm, '')    // Remove trailing spaces and tabs, but preserve newlines
      .trim()
  }
}
